/**
 * Grouped, keyboard-navigable search results, shared by the Cmd-K overlay and the /search route.
 *
 * Every async state is explicit: idle (no term yet), loading (skeleton), error, no-permission, empty
 * (a real query returned nothing), and results. The three groups — members / accounts / invoices —
 * are NEVER merged; each is a labelled section. A PENDING invitee (an email Zoom lists only as an
 * unaccepted invite, API wave 7) is a person found by email, so it sits in the Members section after
 * the members, reading "Pending invite · <account name · number>" and opening that account's Pending tab. account_type is a DECOY and is deliberately NOT shown
 * as a plan (we show the account number / owner instead). Selecting a row calls onSelect with a typed
 * DrillTarget; the caller turns that into navigation via drillPath.
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Building2, FileText, UserRound } from "lucide-react";
import { EmptyState, ErrorState, SkeletonRows } from "@/components/ui";
import { Badge } from "@/components/ui/Badge";
import { RetiredBadge } from "@/components/portal/RetiredBadge";
import { AccountStatusBadge } from "@/components/portal/AccountStatusBadge";
import { statusLabel } from "@/features/customer/display";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import type { InvoiceAttributionState, SearchResult } from "@/api/types";
import type { DrillTarget } from "./drill";
import { MIN_SEARCH_CHARS, searchHitCount } from "./useSearch";
import { useRbac } from "@/context/RbacContext";

interface SearchResultsProps {
  /** The debounced term that produced `data` (for the empty-state copy). */
  term: string;
  active: boolean;
  canSearch: boolean;
  data: SearchResult | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onSelect: (target: DrillTarget) => void;
  onRetry?: () => void;
  /** Compact paddings for the overlay; roomier for the full page. */
  density?: "overlay" | "page";
}

interface FlatRow {
  id: string;
  target: DrillTarget;
}

export function SearchResults({
  term,
  active,
  canSearch,
  data,
  isLoading,
  isError,
  error,
  onSelect,
  onRetry,
  density = "overlay",
}: SearchResultsProps) {
  const { t } = useTranslation();
  const { canSeeMoney } = useRbac();
  // Invoice hits carry NO money (number + date only — no leak), but a non-money role cannot open the
  // Invoice tab, so offering an invoice result would dead-end on Overview. Suppress the group for them
  // rather than invite a surface the role can't use.
  const showInvoices = canSeeMoney();

  // Flat, ordered list (members → accounts → invoices) for stable row ids + future keyboard nav.
  const flat = useMemo<FlatRow[]>(() => {
    if (!data) return [];
    const rows: FlatRow[] = [];
    for (const item of data.members)
      rows.push({ id: `m:${item.account_id}:${item.email}`, target: { kind: "member", item } });
    for (const item of data.pending ?? [])
      rows.push({ id: `p:${item.account_id}:${item.email}`, target: { kind: "pending", item } });
    for (const item of data.accounts)
      rows.push({ id: `a:${item.account_id}`, target: { kind: "account", item } });
    if (showInvoices) {
      // K2-A: an unattributed invoice (account_id null) has no DrillTarget at all — it renders as
      // a static badge (UnattributedInvoiceRow), never a selectable option.
      for (const item of data.invoices) {
        if (item.account_id == null) continue;
        // TS narrows the ACCESS `item.account_id` here to `string`, but not the static type of
        // `item` itself (SearchInvoice has no discriminant to narrow on) — rebuild the object so
        // the narrowed value is what the intersection type actually sees.
        rows.push({
          id: `i:${item.invoice_id}`,
          target: { kind: "invoice", item: { ...item, account_id: item.account_id } },
        });
      }
    }
    return rows;
  }, [data, showInvoices]);

  if (!canSearch) {
    return (
      <EmptyState
        icon={<UserRound className="size-8" />}
        title={t("state.forbiddenTitle")}
        body={t("search.noPermission")}
      />
    );
  }

  if (!active) {
    return (
      <EmptyState
        title={t("search.idleTitle")}
        body={t("search.idleBody", { min: MIN_SEARCH_CHARS })}
      />
    );
  }

  if (isLoading) {
    return <SkeletonRows rows={5} className="px-1 py-2" />;
  }

  if (isError) {
    return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />;
  }

  if (searchHitCount(data) === 0) {
    return <EmptyState title={t("search.emptyTitle")} body={t("search.emptyBody", { term })} />;
  }

  const rowPad = density === "overlay" ? "px-3 py-2" : "px-3 py-2.5";
  // Absent on an API that predates wave 7.
  const pending = data?.pending ?? [];

  return (
    <div className="flex flex-col gap-4" role="listbox" aria-label={t("search.resultsLabel")}>
      {data && data.members.length + pending.length > 0 && (
        <Group label={t("search.groups.members")} count={data.members.length + pending.length}>
          {data.members.map((item) => {
            const row = flat.find((r) => r.id === `m:${item.account_id}:${item.email}`)!;
            return (
              <ResultRow
                key={row.id}
                icon={<UserRound className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />}
                primary={item.email}
                secondary={[item.account_name, item.account_number].filter(Boolean).join(" · ")}
                meta={
                  <>
                    {/* The member's OWN account may be retired — the user status says nothing about it. */}
                    <RetiredBadge
                      is_active={item.is_active}
                      left_list_at={item.left_list_at}
                      compact
                    />
                    {item.user_status ? (
                      <Badge tone={item.user_status === "active" ? "ok" : "neutral"}>
                        {statusLabel(t, item.user_status)}
                      </Badge>
                    ) : null}
                  </>
                }
                hint={t("search.drillHint.member")}
                pad={rowPad}
                onSelect={() => onSelect(row.target)}
              />
            );
          })}
          {pending.map((item) => {
            const row = flat.find((r) => r.id === `p:${item.account_id}:${item.email}`)!;
            const account = [item.account_name, item.account_number].filter(Boolean).join(" · ");
            return (
              <ResultRow
                key={row.id}
                icon={<UserRound className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />}
                primary={item.email}
                secondary={t("search.pendingInvite", { account: account || item.account_id })}
                meta={
                  <RetiredBadge
                    is_active={item.is_active}
                    left_list_at={item.left_list_at}
                    compact
                  />
                }
                hint={t("search.drillHint.pending")}
                pad={rowPad}
                onSelect={() => onSelect(row.target)}
              />
            );
          })}
        </Group>
      )}

      {data && data.accounts.length > 0 && (
        <Group label={t("search.groups.accounts")} count={data.accounts.length}>
          {data.accounts.map((item) => (
            <ResultRow
              key={`a:${item.account_id}`}
              icon={<Building2 className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />}
              primary={item.account_name ?? item.account_number ?? item.account_id}
              // account_type is a DECOY — never shown as the plan. Account number + owner identify it.
              secondary={[item.account_number, item.owner_email].filter(Boolean).join(" · ")}
              // Search is the ONLY path to some accounts (the CustomersPage is search-first — there is no
              // "all customers" list), so a retired account is BADGED here, never filtered out. Hiding it
              // would just make the portal look broken to the operator who typed its name.
              //
              // The SAME rule, twice over, for plan state — and it is a HARD constraint, not a preference:
              // an account whose paid plan Zoom has wrongly marked expired must be FINDABLE by the person
              // who types its name. Filtering it out of search is how you lose a paying customer. Two
              // independent axes, so both badges can appear on one row (retired AND plan-expired).
              //
              // Compact status: "Active" is the norm and shows no badge; only Free / Expired / Unknown
              // do. An expired PAYING account is never hidden — it is findable and simply badged "Expired".
              meta={
                <>
                  <RetiredBadge
                    is_active={item.is_active}
                    left_list_at={item.left_list_at}
                    compact
                  />
                  <AccountStatusBadge
                    plan_state={item.plan_state}
                    base_type={item.base_type}
                    plan_badge={item.plan_badge}
                    compact
                  />
                </>
              }
              hint={t("search.drillHint.account")}
              pad={rowPad}
              onSelect={() => onSelect({ kind: "account", item })}
            />
          ))}
        </Group>
      )}

      {data && showInvoices && data.invoices.length > 0 && (
        <Group label={t("search.groups.invoices")} count={data.invoices.length}>
          {data.invoices.map((item) => {
            // K2-A: a reseller-list-discovered invoice can have NO customer yet (attribution
            // pending or permanently unresolved) or belong to the reseller's own master account —
            // account_id is null either way. Never build a drill target from that (it would
            // navigate to /customers/null); render a non-clickable "unattributed" row instead.
            // Checking account_id (not attribution_state) is what lets TS narrow item.account_id
            // to `string` for the ResultRow branch below.
            if (item.account_id == null) {
              return (
                <UnattributedInvoiceRow
                  key={`i:${item.invoice_id}`}
                  invoiceNumber={item.invoice_number}
                  invoiceDate={item.invoice_date}
                  attributionState={item.attribution_state}
                  pad={rowPad}
                />
              );
            }
            const accountId = item.account_id; // narrowed to `string` by the guard above
            return (
              <ResultRow
                key={`i:${item.invoice_id}`}
                icon={<FileText className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />}
                primary={item.invoice_number}
                secondary={item.invoice_date ? formatDate(item.invoice_date) : ""}
                hint={t("search.drillHint.invoice")}
                pad={rowPad}
                onSelect={() =>
                  onSelect({ kind: "invoice", item: { ...item, account_id: accountId } })
                }
              />
            );
          })}
        </Group>
      )}
    </div>
  );
}

function Group({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1">
      <h3 className="flex items-center gap-2 px-3 text-(length:--text-xs) font-semibold text-(--text-muted)">
        {label}
        <span className="tabular text-(length:--text-2xs) font-normal text-(--text-subtle)">
          {count}
        </span>
      </h3>
      <ul className="flex flex-col">{children}</ul>
    </section>
  );
}

interface ResultRowProps {
  icon: React.ReactNode;
  primary: string;
  secondary?: string;
  meta?: React.ReactNode;
  hint: string;
  pad: string;
  onSelect: () => void;
}

interface UnattributedInvoiceRowProps {
  invoiceNumber: string;
  invoiceDate: string | null;
  attributionState: InvoiceAttributionState | null | undefined;
  pad: string;
}

/** K2-A: an invoice hit with no known customer (account_id null) — a static row, not a button.
 * There is nowhere to drill: /customers/null is not a real page.
 * R7 (final review): a master_account hit (the reseller's own invoice, e.g. 941855) gets a
 * DISTINCT label from a genuinely unattributed one — matching ZPCD's Excel, which already shows
 * "1TOALL master (941855)" for the same invoice (decisions doc ruling 3). Before this fix, both
 * states rendered the same generic "Unattributed" badge, even though attribution_state (a real
 * 3-state value the API already derives) was available — the F2 defect restated in the portal.
 * Absent/other values fall back to the generic label, same as before. */
function UnattributedInvoiceRow({
  invoiceNumber,
  invoiceDate,
  attributionState,
  pad,
}: UnattributedInvoiceRowProps) {
  const { t } = useTranslation();
  const label =
    attributionState === "master_account"
      ? t("search.masterAccountInvoice")
      : t("search.unattributedInvoice");
  return (
    <li role="presentation">
      <div className={cn("flex w-full items-center gap-3 text-left", pad)}>
        <FileText className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-(length:--text-sm) font-medium text-(--text-muted)">
            {invoiceNumber}
          </span>
          {invoiceDate && (
            <span className="truncate text-(length:--text-xs) text-(--text-muted)">
              {formatDate(invoiceDate)}
            </span>
          )}
        </span>
        <Badge tone="neutral">{label}</Badge>
      </div>
    </li>
  );
}

function ResultRow({ icon, primary, secondary, meta, hint, pad, onSelect }: ResultRowProps) {
  return (
    <li role="option" aria-selected={false}>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "group flex w-full items-center gap-3 rounded-(--radius-sm) text-left transition-colors",
          "hover:bg-(--surface-hover) focus-visible:bg-(--surface-hover)",
          "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--brand)",
          pad,
        )}
      >
        {icon}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-(length:--text-sm) font-medium text-(--text)">
            {primary}
          </span>
          {secondary && (
            <span className="truncate text-(length:--text-xs) text-(--text-muted)">
              {secondary}
            </span>
          )}
        </span>
        {meta}
        <span className="hidden shrink-0 text-(length:--text-2xs) text-(--text-subtle) group-hover:inline group-focus-visible:inline">
          {hint}
        </span>
      </button>
    </li>
  );
}
